import { useState, useEffect, useCallback } from 'react';
import payeeCacheService from '../services/payeeCache';
import api from '../services/api';
import { useGroup } from '../contexts/GroupContext.jsx';

export const usePayees = () => {
  const { activeGroupId } = useGroup();
  const [payees, setPayees] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!activeGroupId) return;
    const loadPayees = async () => {
      try {
        setLoading(true);
        setError(null);

        let cached = await payeeCacheService.getAllPayees();
        if (cached) {
          setPayees(cached.payees);
          return;
        }

        const response = await api.get(activeGroupId, '/payees');
        const fetchedPayees = response.payees || [];

        await payeeCacheService.cachePayees(fetchedPayees);
        setPayees(fetchedPayees);
      } catch (err) {
        console.error('Failed to load payees:', err);
        setError('Failed to load payees');
      } finally {
        setLoading(false);
      }
    };

    loadPayees();
  }, [activeGroupId]);

  const addPayee = useCallback(async (payeeName) => {
    try {
      const response = await api.post(activeGroupId, '/payees', { name: payeeName });
      const newPayee = response.payee;

      payeeCacheService.recordPayeeUsage(newPayee.name);
      await payeeCacheService.invalidatePayeeCache('new_payee_added');

      setPayees(prev => {
        const updated = [...prev, newPayee];
        return updated.sort((a, b) => a.name.toLowerCase().localeCompare(b.name.toLowerCase()));
      });

      return newPayee;
    } catch (err) {
      console.error('Failed to create payee:', err);
      throw err;
    }
  }, [activeGroupId]);

  const invalidateCache = () => {
    payeeCacheService.invalidatePayeeCache('manual_invalidation');
  };

  return {
    payees,
    loading,
    error,
    addPayee,
    invalidateCache
  };
};
